import { ComponentType, lazy, LazyExoticComponent, Suspense } from 'react';

import { useAppStore, type IAppStore } from '@/store';
import { Screen } from '@/types';
import { useHostRoutePresetSlug } from '@/features/deeplinks';
import { isHostEmbed } from '@/utils/environment/surface';
import useStyles from './styles';
import type { MobileLayoutProps } from './types';

import MobileCreate from '@/components/MobileCreate';
import ScrollPanel from '@/components/ScrollPanel';

const Loading = lazy(() => import('@/components/Loading'));
const Generations = lazy(() => import('@/components/Generations'));
const CategoryRowsHome = lazy(() => import('@/components/CategoryRowsHome'));

const FilesScreen = () => (
  <ScrollPanel>
    <Generations />
  </ScrollPanel>
);

const selector = (store: IAppStore) => ({
  screen: store.screen,
  isSearchOpen: store.isSearchOpen,
});

const overlayComponents: Partial<
  Record<Screen, LazyExoticComponent<ComponentType> | ComponentType>
> = {
  [Screen.Loading]: Loading,
  [Screen.Files]: FilesScreen,
  [Screen.Library]: CategoryRowsHome,
};

const MobileLayout = ({ hideTrending = false }: MobileLayoutProps) => {
  const { screen, isSearchOpen } = useAppStore(selector);
  const hostPresetSlug = useHostRoutePresetSlug();
  const classes = useStyles();

  // Embedded, the feed shows only while the host is on a preset page. It is a
  // global overlay — portaled to `<body>` at the `popup` layer — so hiding the
  // container gen.ai keeps this app in on every other route does not hide it.
  // `Screen.Create` reaches here without a host preset whenever the app was on
  // the desktop layout (which auto-selects a preset into Create on every page)
  // and the window narrows to mobile: the feed then opened over gen.ai's home.
  const isFeedOpen =
    screen === Screen.Create && (!isHostEmbed() || hostPresetSlug !== null);
  const baseScreen = screen === Screen.Create ? Screen.Library : screen;
  const BaseScreen = overlayComponents[baseScreen];

  const isCovered = isFeedOpen || isSearchOpen;

  return (
    <div className={classes.mobileContent}>
      {BaseScreen && (
        <div className={classes.screenLayer} hidden={isCovered}>
          <Suspense fallback={null}>
            {baseScreen === Screen.Library ? (
              <CategoryRowsHome hideTrending={hideTrending} />
            ) : (
              <BaseScreen />
            )}
          </Suspense>
        </div>
      )}
      {isFeedOpen && <MobileCreate />}
    </div>
  );
};

export default MobileLayout;
